import type { MuiTheme as Theme } from '@paintscout/ui'
import {
  makeStyles,
  Hidden,
  Tab,
  Tabs,
  Typography,
  useClientOptions,
  useUser,
  useUserPermissions,
  useIsMobile
} from '@paintscout/ui'
import QuoteSearchBar, { getFiltersFromLocation } from 'src/components/common/QuoteSearchBar'
import QuoteTable from 'src/components/common/QuoteTable'

import type { SearchQuotesQuery } from '@paintscout/api'
import { useOfflineQuotesQuery } from '@paintscout/api'
import { getFeature, getObjectLabels, getStatusOptions } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util/users'
import * as Sentry from '@sentry/react'
import type { InFilter, ValueFilter } from 'json-to-lucene'
import { jsonToLucene } from 'json-to-lucene'
import { useSnackbar } from 'notistack'
import type { QuoteStatus } from 'paintscout'
import React, { useEffect, useMemo, useState } from 'react'
import { Offline, Online } from 'react-detect-offline'
import { useQuoteMutations, useQuoteSearch } from 'shared/hooks'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import DocumentTitle from 'src/components/common/DocumentTitle'
import BasePage from 'src/components/layouts/BasePage'

import { useApolloClient } from '@apollo/react-hooks'
import { sanitizeFiltersWithAcceptedDate } from '@paintscout/util/util'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { cachedQuotesDelete } from 'src/util/cache'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {},
  paper: {
    boxShadow: theme.shadows[1]
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerLeft: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center'
    }
  },
  newButton: {
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1)
    }
  },
  tabs: {
    paddingLeft: 0,
    marginBottom: 0,
    [theme.breakpoints.down('md')]: {
      '& button': {
        marginRight: theme.spacing(2)
      }
    }
  },
  searchBarFlexContainer: {
    display: 'flex'
  },
  searchBarContainer: {
    flex: '1 1 auto',
    marginRight: 0,
    [theme.breakpoints.down('sm')]: {
      width: '190px'
    }
  },
  newButtonContainer: {
    flex: '0 1 auto',
    height: 'fit-content'
  },
  quoteTableCard: {
    border: `1px solid ${theme.palette.divider}`
  }
}))

function QuoteList() {
  const match = useRouteMatch()
  const { options } = useClientOptions()
  const invoice = match.path.startsWith('/invoices')
  const objectLabels = getObjectLabels({ options, invoice })

  return (
    <BasePage>
      <DocumentTitle title={objectLabels.quote.plural} />
      <Online>
        <OnlineQuotes />
      </Online>
      <Offline>
        <OfflineQuotes />
      </Offline>
    </BasePage>
  )
}

interface UrlParams {
  sort: string
  tab: QuoteStatus | 'all' | 'mine'
  page: number
}

const STATUS_VALUE_ID = 'status.value'

function OnlineQuotes() {
  const classes = useStyles()
  const limit = 20

  const match = useRouteMatch()
  const location = useLocation<{ page?: number; bookmark?: string; sort?: number; fullLoad?: boolean }>()
  const history = useHistory()
  const { options } = useClientOptions()
  const useAcceptedDate = !options?.options?.quotes?.dontCountAcceptedDate
  const { enqueueSnackbar } = useSnackbar()
  const invoice = match.path.startsWith('/invoices')
  const objectLabels = getObjectLabels({ options, invoice })
  const urlParams = getUrlQuery<UrlParams>({ location })
  const client = useApolloClient()
  const { user } = useUser()
  const {
    permissionToEditAssignedQuote,
    permissionToEditAssignedInvoice,
    permissionToEditAllInvoice,
    permissionToEditAllQuote
  } = useUserPermissions()
  const hasMineTab =
    (!invoice && (permissionToEditAssignedQuote || permissionToEditAllQuote)) ||
    (invoice && (permissionToEditAssignedInvoice || permissionToEditAllInvoice))
  const canBulkWrite = (!invoice && permissionToEditAllQuote) || (invoice && permissionToEditAllInvoice)
  const [page, setPage] = useState(location.state?.page ?? 1)
  const [bookmark, setBookmark] = useState(location.state?.bookmark ?? undefined)
  const [filters, setFilters] = useState(getFiltersFromLocation(location))
  const [query, setQuery] = useState('') // will get populated from QuoteSearchBar
  const [sort, setSort] = useState(urlParams.sort ?? '-quote_date')
  const { deleteQuotes } = useQuoteMutations()

  const [tab, setTab] = useState(urlParams.tab ?? 'all')

  const [search, { loading, data, error, fetchMore, updateQuery }] = useQuoteSearch({
    // if we're on page 1 we'll also refetch to get latest results
    fetchPolicy: page === 1 ? (location?.state?.fullLoad ? 'no-cache' : 'cache-and-network') : 'cache-first',
    limit,
    prevBookmark: bookmark,
    prevSort: [sort],
    onCompleted: (newData: SearchQuotesQuery) => {
      setBookmark(newData?.searchQuotes?.bookmark)
      // reset page back to 1 if user refreshes the page (cache is reset)
      if (
        page * limit > newData?.searchQuotes?.quotes?.length &&
        newData?.searchQuotes?.quotes?.length !== newData?.searchQuotes?.total_rows
      ) {
        setPage(1)
      }
    }
  })

  const handleRequestSort = (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => {
    const newSort = `${direction === 'desc' ? '-' : ''}${name}`

    setPage(1)
    setUrlQuery({ location, params: { sort: newSort } })
    setSort(newSort)

    search({
      query,
      sort: [newSort, '-dates.updated']
    })
  }

  // show error toast
  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
      enqueueSnackbar(`Unable to search ${objectLabels.quote.plural}`, { variant: 'error' })
    }
  }, [error])

  // update page param in location.state so it's not in URL
  useEffect(() => {
    if (page || bookmark) {
      history.replace({
        ...history.location,
        state: {
          bookmark,
          page,
          sort
        }
      })
    }
  }, [page, bookmark])

  // // update the tab to reflect the current filter
  useEffect(() => {
    const ownerFilter = filters.find((filter) => filter.id === 'owner.id') as InFilter
    const statusFilter = filters.find((filter) => filter.id === STATUS_VALUE_ID) as InFilter

    const isStatusFilter =
      statusFilter &&
      filters.length === 1 &&
      (typeof statusFilter.value === 'string' || statusFilter.value.length === 1)

    const isMineFilter =
      filters.length === 1 &&
      ownerFilter?.value?.length === 1 &&
      ownerFilter.value[0] === removeAuth0Prefix(user.user_id)

    if (isMineFilter) {
      setTab('mine')
    } else if (isStatusFilter) {
      setTab(typeof statusFilter.value === 'string' ? statusFilter.value : (statusFilter.value[0] as QuoteStatus))
    } else {
      setTab('all')
    }
  }, [])

  const { total_rows = 0, quotes = [] } = data && data.searchQuotes ? data.searchQuotes : {}

  async function handleOnDelete(ids: string[]) {
    try {
      await cachedQuotesDelete(ids, client)
      removeRowsFromQuery(ids)
      await deleteQuotes([...ids], true)
    } catch (e) {
      console.log('delete ids error', { ids })
      Sentry.captureException(e, { tags: { quoteId: ids[0] } })
      enqueueSnackbar(`Unable to delete ${objectLabels.quote.value}`, { variant: 'error' })
    }
  }

  function removeRowsFromQuery(ids: string[]) {
    updateQuery((cache: SearchQuotesQuery) => {
      if (!cache?.searchQuotes) {
        return cache
      }

      return {
        ...cache,
        searchQuotes: {
          ...cache.searchQuotes,
          quotes: cache.searchQuotes.quotes.filter((quote) => !ids.includes(quote._id))
        }
      }
    })
  }

  const statuses = getStatusOptions({ isInvoice: invoice }).filter(
    (status) => status.primary || getFeature({ options, path: status.feature })
  )
  return (
    <>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Hidden smDown={true}>
            <Tabs
              value={tab ?? 'all'}
              classes={{ root: classes.tabs }}
              onChange={(ev, newTab) => {
                setTab(newTab)
                switch (newTab) {
                  case 'mine':
                    setFilters([
                      {
                        id: 'owner.id',
                        type: 'in',
                        value: [removeAuth0Prefix(user.user_id)]
                      }
                    ])
                    break
                  case 'all':
                    setFilters([])
                    break
                  default:
                    setFilters([
                      {
                        id: STATUS_VALUE_ID,
                        type: 'in',
                        value: [newTab]
                      }
                    ])
                }
                // this is gross, but it lets QuoteSearchBar render the new filters
                // before the tab changes - so we don't see a brief flicker of the previously
                // hidden filters when switching from status/mine -> all
                setTimeout(() => setTab(newTab))
              }}
            >
              <Tab value="all" label="All" />
              {hasMineTab && <Tab value="mine" label="Mine" />}
              <Tab disabled label="|" />
              {statuses.map((status) => (
                <Tab key={status.value} value={status.value} label={status.label} />
              ))}
            </Tabs>
          </Hidden>
        </div>
      </div>
      <div className={classes.searchBarFlexContainer}>
        <div className={classes.searchBarContainer}>
          <QuoteSearchBar
            loading={loading}
            invoice={invoice}
            filters={filters}
            onSearch={(text, filters, isInitialSearch) => {
              let adjustedFilters = [
                ...filters,
                {
                  id: 'trashed',
                  type: 'equal',
                  not: true,
                  value: true
                },
                {
                  id: 'is_invoice',
                  type: 'equal',
                  not: !invoice,
                  value: true
                }
              ] as ValueFilter[]

              // add implicit archived filters to search if necessary
              if (!filters.find((filter) => filter.id === 'archived')) {
                if (text) {
                  adjustedFilters = [
                    ...adjustedFilters,
                    {
                      id: 'archived',
                      type: 'in',
                      value: ['true', 'false']
                    }
                  ]
                } else {
                  adjustedFilters = [
                    ...adjustedFilters,
                    {
                      id: 'archived',
                      type: 'equal',
                      value: 'false'
                    }
                  ]
                }
              }
              const query =
                jsonToLucene({
                  query: text ?? '',
                  filters: useAcceptedDate ? sanitizeFiltersWithAcceptedDate(adjustedFilters) : adjustedFilters
                }) || '*:*'
              setQuery(query)
              setFilters(filters)
              setSort(sort)

              if (!isInitialSearch) {
                setPage(1)
              }

              const existingMineFilter = filters.find(
                (filter) => filter.id === 'owner.id' && filter.value === removeAuth0Prefix(user.user_id)
              ) as InFilter
              const existingStatusFilter = filters.find((filter) => filter.id === STATUS_VALUE_ID) as InFilter

              if (!existingMineFilter && !existingStatusFilter) {
                setTab('all')
              }

              if (existingStatusFilter?.value?.length > 0 && !existingStatusFilter.value.includes(tab)) {
                setTab(existingStatusFilter.value[0] as QuoteStatus)
              }

              search({
                query,
                sort: [sort, '-dates.updated'],
                limit
              })
            }}
          />
        </div>
      </div>
      <QuoteTable
        classes={{ quoteTableCard: classes.quoteTableCard }}
        allowCustomColumns={getFeature({ options, path: 'customQuoteTableColumns' })}
        loading={loading}
        quotes={quotes as any[]}
        rowsPerPage={limit}
        totalRows={total_rows}
        page={page}
        pages={total_rows < limit ? 1 : Math.min(Math.ceil(total_rows / limit), 20)} // max of 20 pages (200 results with limit of 10)
        checkboxes={canBulkWrite}
        bulkActions={{
          delete: true,
          archive: true,
          unarchive: true
        }}
        actionsForRow={(row) => ({
          delete: true,
          edit: true,
          copy: true,
          archive: !row.archived,
          unarchive: row.archived
        })}
        isInvoice={invoice}
        onPageChange={setPage}
        onFetchNextPage={() => {
          fetchMore()
        }}
        sort={sort}
        onDelete={async (ids) => await handleOnDelete(ids)}
        onArchive={(ids) => removeRowsFromQuery(ids)}
        onRequestSort={handleRequestSort}
      />
    </>
  )
}

function OfflineQuotes() {
  const classes = useStyles()
  const match = useRouteMatch()
  const invoice = match.path.startsWith('/invoices')
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options, invoice })
  const { data, error } = useOfflineQuotesQuery({ fetchPolicy: 'cache-and-network' })
  const { permissionToEditAllInvoice, permissionToEditAllQuote } = useUserPermissions()
  const canBulkWrite = (!invoice && permissionToEditAllQuote) || (invoice && permissionToEditAllInvoice)
  const isSmallScreen = useIsMobile({ custom: 1024 })
  const quoteType = invoice ? 'invoices' : 'quotes'

  const quotes = useMemo(
    () =>
      data?.offlineQuotes
        ? data.offlineQuotes[quoteType].sort((a, b) => (a.dates.updated > b.dates.updated ? -1 : 1))
        : [],
    [data, quoteType]
  )

  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
    }
  }, [error])

  return (
    <>
      <Typography variant="subtitle1" align="center" gutterBottom>
        You are offline - only recent {objectLabels.quote.plural} are available
      </Typography>

      <QuoteTable
        classes={{ card: classes.quoteTableCard }}
        hideHeaders={{ ['location']: isSmallScreen }}
        checkboxes={canBulkWrite}
        bulkActions={{
          delete: true,
          archive: true
        }}
        actionsForRow={() => ({
          delete: true,
          edit: true,
          copy: true,
          archive: true
        })}
        quotes={quotes}
        totalRows={quotes.length}
        rowsPerPage={quotes.length}
        page={1}
        pages={1}
      />
    </>
  )
}

export default QuoteList
